import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Set } from 'immutable';

import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import AddIcon from '@material-ui/icons/AddCircleOutline';
import ButtonBase from '@material-ui/core/ButtonBase';

// Data
import { useDispatch } from 'react-redux';
import { createBillScoutSchedule, getScheduledTransactions } from 'data/scheduledTransactions/scheduledTransactionsActions';

// CUSTOM
import receiving from 'assets/onboarding/receiving.svg';
import invoice from 'assets/onboarding/invoice.svg';
import QButton from 'components/QButton';
import ScheduledDialog, { ScheduledDialogType } from 'components/GettingStartedAcme/ScheduledDialog';
import { formatNumber } from 'components/QuickenControls/AmountField';
import tracker from 'utils/tracker';
import RecurringChip from './RecurringChip';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    height: 'calc(100% + 24px)',
  },
  black: {
    color: theme.palette.greyScale[0],
  },
  grey: {
    color: theme.palette.greyScale[1],
  },
  bolder: {
    color: theme.palette.greyScale[0],
    fontWeight: 500,
  },
  spacedRow: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  listWrapper: {
    marginTop: 8,
    flexGrow: 1,
    position: 'relative',
    overflow: 'hidden',
    height: 'calc(100% - 72px)',
  },
  modelList: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    alignContent: 'flex-start',
    overflow: 'scroll',
    height: '100%',
    paddingBottom: 16,
  },
  bottomFade: {
    zIndex: 1,
    position: 'absolute',
    bottom: 0,
    height: 24,
    width: '100%',
    backgroundImage: `linear-gradient(${theme.applyOpacityToHex(theme.palette.greyScale[7], 0)}, ${theme.palette.greyScale[7]})`,
  },

  show: {
    opacity: 1,
    transition: 'opacity 0.4s ease',
  },
  hide: {
    opacity: 0,
    transition: 'opacity 0.4s ease',
  },

  chip: ({isBill}) => ({
    overflow: 'hidden',
    width: 'calc(50% - 16px)',
    margin: 8,
    height: 80,
    borderRadius: 8,
    backgroundColor: isBill ? theme.applyOpacityToHex(theme.palette.secondary.main, 0.8) : theme.applyOpacityToHex(theme.palette.others.positive, 0.7),
    padding: '16px 20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    color: theme.palette.greyScale[7],
    position: 'relative',
    transition: 'background-color 0.3s ease',
    '&:hover': {
      backgroundColor: isBill ? theme.applyOpacityToHex(theme.palette.secondary.dark, 0.8) : theme.applyOpacityToHex(theme.palette.others.positive, 0.9),
    },
  }),
  slash: ({isBill}) => ({
    position: 'absolute',
    left: -10,
    height: 92,
    width: 52,
    backgroundColor: isBill ? theme.applyOpacityToHex(theme.palette.secondary.dark, 0.2) : theme.applyOpacityToHex(theme.palette.others.positive, 0.6),
    transform: 'rotate(-12deg)',
  }),
  addName: {
    marginRight: 80,
    fontWeight: 500,
  },
  icon: ({isBill}) => ({
    height: 48,
    width: 48,
    borderRadius: 8,
    padding: 8,
    zIndex: 1,
    backgroundColor: isBill ? theme.applyOpacityToHex(theme.palette.secondary.dark, 0.6) : theme.applyOpacityToHex(theme.palette.others.positive, 1),
  }),
  bigAdd: {
    height: 32,
    width: 32,
  },
}));

function monthlyTotal(scheduledTransactions) {
  let totalAdd = 0;
  scheduledTransactions.forEach((schedule) => {
    const amt = schedule.transaction.amount;
    const { recurrence } = schedule;
    switch (recurrence.frequency) {
      case 'WEEKLY':
        totalAdd += (4/recurrence.interval) * amt;
        break;
      case 'YEARLY':
        totalAdd += (recurrence.interval/12) * amt;
        break;
      case 'MONTHLY':
        totalAdd += amt/recurrence.interval;
        break;
      case 'DAILY':
        totalAdd += (30/recurrence.interval) * amt;
        break;
      default:
        break;
    }
  });
  return formatNumber(totalAdd, 'USD', '0,0');
}

// Functional Component
// - - - - - - - - - - - - - - - - - - - - - - - - - -
export default function RecurringPage(props) {
  const { scheduledTxns, isBill, style } = props;
  const classes = useStyles({isBill});
  const dispatch = useDispatch();

  const [add, setAdd] = useState(false);

  // mixpanel state things
  const [numAdded, setNumAdded] = useState(0);
  const [keepers, setKeepers] = useState(new Set());
  const [seen, setSeen] = useState(new Set());

  const latestCapture = useRef();
  const capture = {
    total: scheduledTxns.size,
    keep: keepers.size,
    added: numAdded,
  };
  latestCapture.current = capture;

  // send mixpanel data on unmount
  useEffect(() => () => {
    const { total, keep, added } = latestCapture.current;
    if (isBill) {
      tracker.track(tracker.events.addedNewBill, { number: added });
      tracker.track(tracker.events.confirmedBill, { number: keep });
      tracker.track(tracker.events.removedBill, { number: total - keep });
    } else {
      tracker.track(tracker.events.addedNewIncome, { number: added });
      tracker.track(tracker.events.confirmedIncome, { number: keep });
      tracker.track(tracker.events.removedIncome, { number: total - keep });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let newKeepers = keepers;
    let newSeen = seen;
    scheduledTxns.forEach((model) => {
      if (!seen.has(model.id)) {
        newSeen = newSeen.add(model.id);
        if (model.isUserVerified) {
          newKeepers = newKeepers.add(model.id)
        }
      }
    });
    setKeepers(newKeepers);
    setSeen(newSeen);
  }, [scheduledTxns]); // eslint-disable-line react-hooks/exhaustive-deps

  const addSchedule = (currentTxn) => {
    dispatch(createBillScoutSchedule({id: currentTxn.id}));
    setNumAdded(numAdded+1);
    setTimeout(() => dispatch(getScheduledTransactions()), 0);
  };

  const reportSchedule = (id, keep) => {
    if (keep) {
      setKeepers(keepers.add(id));
    } else {
      setKeepers(keepers.delete(id));
    }
  };

  const renderAdd = () =>
    <ButtonBase
      focusRipple
      id={isBill ? "add-bill-button" : "add-income-button"}
      className={classes.chip}
      onClick={()=> setAdd(true)}
    >
      <div className={classes.slash}/>

      <img alt="Add Banner" src={isBill ? invoice : receiving} className={classes.icon}/>
      <Typography variant="body1" className={classes.addName}>
        {isBill ? 'ADD BILL' : 'ADD INCOME'}
      </Typography>
      <AddIcon className={classes.bigAdd}/>
    </ButtonBase>;

  const smallAdd = scheduledTxns.size > 5;
  return (
    <div className={classes.root} style={style}>
      <Typography variant="h6" className={classes.black}>
        {isBill ? 'Confirm your Bills, Subscriptions, and Transfers' : 'Confirm your Income'}
      </Typography>
      <div className={classes.spacedRow} style={{ marginTop: -4 }}>
        <Typography variant="body1" className={classes.grey}>
          {"So far we've found: "} <span className={classes.bolder}>{monthlyTotal(scheduledTxns)}</span>/month
        </Typography>
        <QButton
          className={smallAdd ? classes.show : classes.hide}
          disabled={!smallAdd}
          onClick={()=> setAdd(true)}
        >
          {isBill ? 'ADD BILL' : 'ADD INCOME'} <AddIcon style={{ marginLeft: 4 }}/>
        </QButton>
      </div>

      <div className={classes.listWrapper}>
        <div className={classes.modelList}>
          {scheduledTxns.toIndexedSeq().map((model, index) =>
            <RecurringChip model={model} index={index} key={model.id} isBill={isBill} reportFunc={reportSchedule}/>)
          }
          {renderAdd()}
        </div>

        <div className={classes.bottomFade}/>
      </div>
      <ScheduledDialog
        type={isBill ? ScheduledDialogType.BILL : ScheduledDialogType.INCOME}
        closeFunc={()=> setAdd(false)}
        goFunc={addSchedule}
        selectedSet={scheduledTxns}
        open={add}
        hideBackdrop
      />
    </div>
  );
}

RecurringPage.defaultProps = {
  isBill: false,
};

RecurringPage.propTypes = {
  isBill: PropTypes.bool,
  scheduledTxns: PropTypes.object,
  style: PropTypes.object,
};
